import React, { useState, useMemo, useEffect } from "react";
import "./mealplanner.css";
import {
  Star, Plus, Pencil, Trash2, ChefHat, CalendarDays, ShoppingCart,
  Sparkles, X, Search, RotateCcw, Send, Loader2, Salad, Utensils, Check, Printer,
  Sandwich, Wand2, ArrowRight, Tag, ChevronLeft, ChevronRight
} from "lucide-react";

/* ---------------------------------------------------------------
   MOCK-DATA NOTICE
   Everything in this file is illustrative. The AI panel simulates
   responses with setTimeout instead of calling a real endpoint, so
   you can see the shape of "apply to board" and "add to recipe box"
   before we wire up D1 / KV / the Worker proxy for real.
--------------------------------------------------------------- */

/* ---------------------------------------------------------------
   TOKENS  (unchanged from the original — same identity, extended)
--------------------------------------------------------------- */
const COLOR = {
  paper: "#FAF6EC",
  paperDim: "#F1EAD9",
  ink: "#2A2420",
  inkSoft: "#6B6055",
  rail: "#23201B",
  railSoft: "#3A3329",
  card: "#FFFDF8",
  gold: "#B8873B",
  goldDeep: "#8F6526",
  sage: "#6F8763",
  rust: "#A8503B",
  teal: "#3E7C7C",
  divider: "#E4D9C4",
  cork: "#4A3B2A",
};

/* Real names now. schoolAge flags who gets a Lunch Board row —
   Camden (3) and Austin (1) still tag on dinners, just no lunch row. */
const FAMILY = [
  { id: "sean",   name: "Sean",   color: "#B8873B", schoolAge: false, isParent: true },
  { id: "brenna", name: "Brenna", color: "#6F8763", schoolAge: false, isParent: true },
  { id: "owen",   name: "Owen",   age: 15, color: "#A8503B", schoolAge: true },
  { id: "ben",    name: "Ben",    age: 13, color: "#3E7C7C", schoolAge: true },
  { id: "landon", name: "Landon", age: 11, color: "#7A4A6B", schoolAge: true },
  { id: "dallin", name: "Dallin", age: 8,  color: "#4C6B8A", schoolAge: true },
  { id: "olivia", name: "Olivia", age: 6,  color: "#C77B54", schoolAge: true },
  { id: "camden", name: "Camden", age: 3,  color: "#5B6670", schoolAge: false },
  { id: "austin", name: "Austin", age: 1,  color: "#7A3B3B", schoolAge: false },
];

const LUNCH_ROWS = FAMILY.filter((f) => f.isParent || f.schoolAge).map((f) => f.id); // sean + 5 kids

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// JS getDay() is Sun-first (0-6); DAYS above is Mon-first, so today's index
// needs remapping. Used only to pick a sensible default for the mobile
// Lunch board's day-at-a-time pager.
function DAY_ABBR_FOR_TODAY() {
  const jsDay = new Date().getDay(); // 0 = Sun
  return DAYS[jsDay === 0 ? 6 : jsDay - 1];
}

const STORE_FOR = (cat) => (cat === "produce" ? "Harris Teeter" : "Target");

/* Faceted attributes — curated-but-growable, not free text. Adding a
   new type later (e.g. "Spice Level") is one entry here, no schema
   change, no code change to the filter UI (it's built dynamically). */
const ATTRIBUTE_TYPES = [
  { id: "cuisine", label: "Cuisine", icon: Tag, values: ["Italian", "Mexican", "American", "Asian", "BBQ", "Breakfast", "Mediterranean"] },
  { id: "protein", label: "Main Protein", icon: Utensils, values: ["Beef", "Chicken", "Pork", "Seafood", "Meatless"] },
  { id: "course", label: "Course", icon: Salad, values: ["Main", "Soup", "Salad", "Appetizer", "Bread", "Dessert"] },
];

/* ---------------------------------------------------------------
   HELPERS
--------------------------------------------------------------- */
const findMeal = (meals, id) => meals.find((m) => m.id === id) || null;
const findFamily = (id) => FAMILY.find((f) => f.id === id);

function daysAgo(dateStr) {
  if (!dateStr) return Infinity;
  const diff = (Date.now() - new Date(dateStr + "T12:00:00").getTime()) / 86400000;
  return Math.round(diff);
}

function mondayOfThisWeek() {
  const d = new Date();
  const day = d.getDay();
  const diffToMon = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMon);
  return d;
}

// Sums quantities like "2 lb" + "1.5 lb" into "3.5 lb". Groups by unit so
// "2 lb" + "1 jar" becomes "2 lb, 1 jar" rather than nonsense arithmetic.
// Anything that doesn't parse as a leading number (e.g. "to taste") is left
// as-is and appended after the summed amounts.
function combineQuantities(qtyList) {
  const totals = {};
  const unparsed = [];
  qtyList.forEach((qty) => {
    const match = qty.match(/^([\d.]+)\s*(.*)$/);
    if (match) {
      const num = parseFloat(match[1]);
      const unit = match[2].trim();
      totals[unit] = (totals[unit] || 0) + num;
    } else if (qty.trim()) {
      unparsed.push(qty.trim());
    }
  });
  const summed = Object.entries(totals).map(([unit, total]) => {
    const rounded = Math.round(total * 100) / 100;
    return unit ? `${rounded} ${unit}` : `${rounded}`;
  });
  return [...summed, ...unparsed].join(", ");
}

// Aggregates the grocery list's raw contributions (one entry per meal per
// source day, see addDaysToGroceryList below) into a display-ready list
// grouped by ingredient. Ingredient details resolve LIVE from the meals
// catalog each time this runs — same as before — so fixing a typo in a
// recipe's ingredient list still updates anything already on the grocery
// list, rather than freezing ingredient data at the moment it was added.
// What IS frozen is which meal/day contributed each entry, that's the part
// that has to stay put until you explicitly add or remove it.
function computeGroceryList(entries, meals) {
  const items = {};
  entries.forEach((entry) => {
    const meal = findMeal(meals, entry.mealId);
    if (!meal) return; // meal was deleted since being added to the list — skip gracefully
    meal.ingredients.forEach((ing) => {
      const key = ing.name.toLowerCase() + "|" + ing.category;
      if (!items[key]) items[key] = { name: ing.name, category: ing.category, entries: [] };
      items[key].entries.push({ qty: ing.qty, meal: entry.mealLabel || meal.name, source: entry.sourceKey });
    });
  });
  return Object.values(items).sort((a, b) => a.name.localeCompare(b.name));
}

// Expands one day's board slot (dinner, side, everyone's lunches) into raw
// grocery-list entries, all tagged with the same sourceKey (weekKey:day).
// That shared sourceKey is what makes "Add" an upsert: re-adding a day
// after changing its meals first strips any entries already tagged with
// that sourceKey, then adds these fresh ones — so swapping a meal choice
// is just "change the board, click Add again," no separate remove step
// required unless you actually want the day off the list entirely.
function buildDayEntries(weekKey, day, slot) {
  const sourceKey = `${weekKey}:${day}`;
  const entries = [];
  if (slot.dinner) entries.push({ sourceKey, weekKey, day, mealId: slot.dinner, mealLabel: null });
  if (slot.side) entries.push({ sourceKey, weekKey, day, mealId: slot.side, mealLabel: null });
  (slot.seanLunch || []).forEach((id) => entries.push({ sourceKey, weekKey, day, mealId: id, mealLabel: "Sean's lunch" }));
  Object.entries(slot.lunches || {}).forEach(([personId, ids]) => {
    const person = findFamily(personId)?.name || personId;
    (ids || []).forEach((id) => entries.push({ sourceKey, weekKey, day, mealId: id, mealLabel: `${person}'s lunch` }));
  });
  return entries;
}

/* ---------------------------------------------------------------
   SMALL UI PRIMITIVES
--------------------------------------------------------------- */
function FamilyDot({ id, size = 18 }) {
  const f = findFamily(id);
  if (!f) return null;
  return (
    <span
      title={f.name}
      className="inline-flex items-center justify-center rounded-full font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42, background: f.color, color: "#FFFDF8", fontFamily: "'Inter', sans-serif" }}
    >
      {f.name[0]}
    </span>
  );
}

function Pill({ children, color }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: color + "22", color, fontFamily: "'Inter', sans-serif" }}>
      {children}
    </span>
  );
}

function PrimaryButton({ children, onClick, icon: Icon, style = {}, disabled, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-transform active:scale-95 disabled:opacity-50"
      style={{ background: COLOR.gold, color: "#231C10", fontFamily: "'Inter', sans-serif", ...style }}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, icon: Icon, danger, active }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors"
      style={{
        borderColor: danger ? COLOR.rust : active ? COLOR.gold : COLOR.divider,
        background: active ? COLOR.gold + "22" : "transparent",
        color: danger ? COLOR.rust : COLOR.inkSoft,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {Icon && <Icon size={13} />}
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------
   WAKE LOCK  (backs the "keep screen on" switch in Cook Mode)
   Screen Wake Lock API releases itself whenever the tab goes into the
   background (app switch, screen lock, etc.) — that's a browser security
   requirement, not a bug. wantedRef tracks whether the COOK wants it on
   independent of whether the OS just yanked it, so coming back to the tab
   re-acquires automatically instead of silently leaving the screen
   unprotected.
--------------------------------------------------------------- */
function useWakeLock() {
  const supported = typeof navigator !== "undefined" && "wakeLock" in navigator;
  const [active, setActive] = useState(false);
  // Set only when a request the cook actually asked for gets denied (e.g. by
  // the OS or a permissions policy) — distinct from "off" — so the UI can
  // tell them the screen might NOT actually stay on, rather than silently
  // leaving an unchecked box that looks like "never tried."
  const [error, setError] = useState(false);
  const lockRef = React.useRef(null);
  const wantedRef = React.useRef(false);

  const acquire = async () => {
    try {
      const lock = await navigator.wakeLock.request("screen");
      lockRef.current = lock;
      lock.addEventListener("release", () => { lockRef.current = null; });
      setActive(true);
      setError(false);
    } catch {
      setActive(false);
      if (wantedRef.current) setError(true);
    }
  };

  const release = async () => {
    wantedRef.current = false;
    try { await lockRef.current?.release(); } catch {}
    lockRef.current = null;
    setActive(false);
    setError(false);
  };

  const toggle = () => {
    if (wantedRef.current) release();
    else { wantedRef.current = true; setError(false); acquire(); }
  };

  useEffect(() => {
    if (!supported) return;
    const onVisibility = () => {
      if (document.visibilityState === "visible" && wantedRef.current && !lockRef.current) acquire();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      wantedRef.current = false;
      lockRef.current?.release().catch(() => {});
    };
  }, [supported]);

  return { supported, active, error, toggle };
}

/* ---------------------------------------------------------------
   COOK MODE  (full-screen, large-type view for actually cooking from)
--------------------------------------------------------------- */
function CookModeModal({ meal, onClose }) {
  const { supported, active, error: wakeLockError, toggle } = useWakeLock();
  const steps = (meal.instructions || "")
    .split(/\n+/)
    .map((s) => s.trim().replace(/^\d+[.)]\s*/, ""))
    .filter(Boolean);
  const attrs = [meal.attributes?.cuisine, meal.attributes?.protein, meal.attributes?.course].filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto no-print" style={{ background: COLOR.paper }}>
      <div className="sticky top-0 flex items-center justify-between gap-3 px-5 py-4 border-b flex-wrap" style={{ background: COLOR.paper, borderColor: COLOR.divider }}>
        <div>
          <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>Cook mode</p>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: COLOR.ink }}>{meal.name}</h2>
        </div>
        <div className="flex items-center gap-4">
          <label
            className="flex items-center gap-2 text-sm select-none"
            style={{ color: supported ? COLOR.ink : COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}
            title={supported ? "Keeps this device's screen from sleeping while Cook Mode is open" : "Not supported in this browser"}
          >
            <input type="checkbox" checked={active} disabled={!supported} onChange={toggle} style={{ width: 18, height: 18 }} />
            Keep screen on
          </label>
          <button onClick={onClose} title="Close"><X size={24} color={COLOR.inkSoft} /></button>
        </div>
      </div>

      {!supported && (
        <p className="px-5 pt-3 text-xs" style={{ color: COLOR.rust, fontFamily: "'Inter', sans-serif" }}>
          This browser can't keep the screen on automatically — you may want to adjust your device's auto-lock setting before you start.
        </p>
      )}
      {supported && wakeLockError && (
        <p className="px-5 pt-3 text-xs" style={{ color: COLOR.rust, fontFamily: "'Inter', sans-serif" }}>
          Couldn't keep the screen on — your browser or device denied the request. Try again, or adjust your auto-lock setting before you start.
        </p>
      )}

      <div className="max-w-2xl mx-auto p-5 pb-16">
        {attrs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {meal.attributes?.cuisine && <Pill color={COLOR.goldDeep}>{meal.attributes.cuisine}</Pill>}
            {meal.attributes?.protein && <Pill color={COLOR.rust}>{meal.attributes.protein}</Pill>}
            {meal.attributes?.course && <Pill color={COLOR.teal}>{meal.attributes.course}</Pill>}
          </div>
        )}
        {meal.notes && <p className="text-sm italic mb-6" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>{meal.notes}</p>}

        {meal.ingredients.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>Ingredients</h3>
            <ul className="space-y-2.5">
              {meal.ingredients.map((ing, i) => (
                <li key={i} className="flex items-baseline gap-2" style={{ color: COLOR.ink, fontFamily: "'Inter', sans-serif", fontSize: 18 }}>
                  <span style={{ color: COLOR.goldDeep }}>•</span>
                  <span><strong>{ing.qty}</strong> {ing.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>Instructions</h3>
          {steps.length > 0 ? (
            <ol className="space-y-4">
              {steps.map((step, i) => (
                <li key={i} className="flex gap-3" style={{ color: COLOR.ink, fontFamily: "'Inter', sans-serif", fontSize: 18, lineHeight: 1.5 }}>
                  <span className="shrink-0 flex items-center justify-center rounded-full font-bold" style={{ width: 28, height: 28, background: COLOR.gold, color: "#231C10", fontSize: 14 }}>{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>No instructions saved for this recipe yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   RECIPE CARD
--------------------------------------------------------------- */
function RecipeCard({ meal, onEdit, onDelete, onToggleFavorite, onCook, rotate }) {
  const [showRecipe, setShowRecipe] = useState(false);
  const tabColor = meal.category === "dinner" ? COLOR.gold : meal.category === "side" ? COLOR.sage : COLOR.teal;
  const age = daysAgo(meal.lastUsed);
  return (
    <div
      className="relative rounded-lg p-4 pl-5 shadow-sm hover:shadow-md transition-shadow"
      style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}`, transform: rotate ? `rotate(${rotate}deg)` : undefined }}
    >
      <div className="absolute left-0 top-3 bottom-3 w-1.5 rounded-full" style={{ background: tabColor }} />
      <button onClick={() => onToggleFavorite(meal.id)} className="absolute top-3 right-3" title={meal.favorite ? "Remove favorite" : "Mark favorite"}>
        <Star size={18} fill={meal.favorite ? COLOR.gold : "none"} color={meal.favorite ? COLOR.gold : COLOR.inkSoft} />
      </button>

      <div className="flex items-center gap-2 mb-1 pr-6">
        <h3 className="font-semibold leading-snug" style={{ fontFamily: "'Fraunces', serif", color: COLOR.ink, fontSize: 17 }}>{meal.name}</h3>
      </div>

      <div className="flex items-center gap-2 mb-2 text-xs" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>
        <span className="uppercase tracking-wide" style={{ color: tabColor }}>{meal.category}</span>
        <span>·</span>
        <span>{meal.lastUsed ? `used ${age}d ago` : "never used"}</span>
      </div>

      {(meal.attributes?.cuisine || meal.attributes?.protein || meal.attributes?.course) && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {meal.attributes?.cuisine && <Pill color={COLOR.goldDeep}>{meal.attributes.cuisine}</Pill>}
          {meal.attributes?.protein && <Pill color={COLOR.rust}>{meal.attributes.protein}</Pill>}
          {meal.attributes?.course && <Pill color={COLOR.teal}>{meal.attributes.course}</Pill>}
        </div>
      )}

      {meal.notes && <p className="text-xs mb-2 italic" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>{meal.notes}</p>}

      <div className="flex flex-wrap gap-1 mb-3">{meal.tags.map((t) => <FamilyDot key={t} id={t} />)}</div>

      {meal.ingredients.length > 0 && (
        <p className="text-xs mb-2" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>{meal.ingredients.length} ingredients</p>
      )}

      {meal.instructions && (
        <div className="mb-3">
          <button onClick={() => setShowRecipe((v) => !v)} className="text-xs flex items-center gap-1" style={{ color: COLOR.teal, fontFamily: "'Inter', sans-serif" }}>
            <ChefHat size={12} /> {showRecipe ? "Hide recipe" : "View recipe"}
          </button>
          {showRecipe && (
            <p className="text-xs mt-1.5 p-2 rounded" style={{ background: COLOR.paperDim, color: COLOR.ink, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
              {meal.instructions}
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <GhostButton icon={ChefHat} onClick={() => onCook(meal)}>Cook</GhostButton>
        <GhostButton icon={Pencil} onClick={() => onEdit(meal)}>Edit</GhostButton>
        <GhostButton icon={Trash2} danger onClick={() => onDelete(meal.id)}>Delete</GhostButton>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   MEAL EDIT MODAL
--------------------------------------------------------------- */
function MealModal({ meal, onSave, onClose }) {
  const isNew = meal === "new" || meal?.prefill;
  const base = meal === "new"
    ? { id: "m-" + Date.now(), name: "", category: "dinner", favorite: false, lastUsed: null, notes: "", instructions: "", tags: [], attributes: {}, ingredients: [{ name: "", qty: "", category: "pantry" }] }
    : meal?.prefill
      ? { ...meal.prefill, id: "m-" + Date.now(), tags: meal.prefill.tags || [] }
      : { ...meal, ingredients: meal.ingredients.length ? [...meal.ingredients] : [{ name: "", qty: "", category: "pantry" }] };
  const [form, setForm] = useState(base);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAttribute = (typeId, value) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [typeId]: value } }));
  const toggleTag = (id) => setForm((f) => ({ ...f, tags: f.tags.includes(id) ? f.tags.filter((t) => t !== id) : [...f.tags, id] }));

  const updateIngredient = (idx, key, value) => setForm((f) => { const next = [...f.ingredients]; next[idx] = { ...next[idx], [key]: value }; return { ...f, ingredients: next }; });
  const addIngredientRow = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { name: "", qty: "", category: "pantry" }] }));
  const removeIngredientRow = (idx) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));

  const save = () => {
    if (!form.name.trim()) return;
    onSave({ ...form, ingredients: form.ingredients.filter((i) => i.name.trim()) });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "#00000066" }}>
      <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ background: COLOR.paper }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: COLOR.divider }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, color: COLOR.ink }}>
            {isNew ? (meal?.prefill ? "Confirm new meal" : "Add a meal") : "Edit meal"}
          </h2>
          <button onClick={onClose}><X size={20} color={COLOR.inkSoft} /></button>
        </div>

        <div className="p-5 space-y-4" style={{ fontFamily: "'Inter', sans-serif" }}>
          {meal?.prefill && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs" style={{ background: COLOR.teal + "18", color: COLOR.teal, fontFamily: "'Inter', sans-serif" }}>
              <Wand2 size={14} /> Suggested by the AI panel. Review and adjust before saving.
            </div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLOR.inkSoft }}>Name</label>
            <input value={form.name} onChange={(e) => setField("name", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }} placeholder="e.g. Shrimp Tacos" />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLOR.inkSoft }}>Category</label>
              <select value={form.category} onChange={(e) => setField("category", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }}>
                <option value="dinner">Dinner</option>
                <option value="side">Side</option>
                <option value="lunch">Lunch</option>
              </select>
            </div>
            <div className="flex items-end pb-2 gap-2">
              <input type="checkbox" checked={form.favorite} onChange={(e) => setField("favorite", e.target.checked)} id="fav" />
              <label htmlFor="fav" className="text-sm" style={{ color: COLOR.ink }}>Favorite</label>
            </div>
          </div>

          <div className="flex gap-4">
            {ATTRIBUTE_TYPES.map((type) => (
              <div className="flex-1" key={type.id}>
                <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLOR.inkSoft }}>{type.label}</label>
                <select value={form.attributes?.[type.id] || ""} onChange={(e) => setAttribute(type.id, e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }}>
                  <option value="">—</option>
                  {type.values.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLOR.inkSoft }}>Who likes it</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {FAMILY.map((f) => (
                <button key={f.id} onClick={() => toggleTag(f.id)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border" style={{ borderColor: form.tags.includes(f.id) ? f.color : COLOR.divider, background: form.tags.includes(f.id) ? f.color + "22" : "transparent", color: COLOR.ink }}>
                  <FamilyDot id={f.id} size={14} /> {f.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLOR.inkSoft }}>Ingredients</label>
              <button onClick={addIngredientRow} className="text-xs flex items-center gap-1" style={{ color: COLOR.goldDeep }}><Plus size={13} /> Add row</button>
            </div>
            <div className="space-y-2 mt-2">
              {form.ingredients.map((ing, idx) => (
                <div key={idx} className="flex gap-2">
                  <input value={ing.name} onChange={(e) => updateIngredient(idx, "name", e.target.value)} placeholder="Ingredient" className="flex-1 px-2 py-1.5 rounded border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }} />
                  <input value={ing.qty} onChange={(e) => updateIngredient(idx, "qty", e.target.value)} placeholder="Qty" className="w-20 px-2 py-1.5 rounded border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }} />
                  <select value={ing.category} onChange={(e) => updateIngredient(idx, "category", e.target.value)} className="px-2 py-1.5 rounded border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }}>
                    <option value="produce">Produce</option>
                    <option value="protein">Protein</option>
                    <option value="dairy">Dairy</option>
                    <option value="pantry">Pantry</option>
                  </select>
                  <button onClick={() => removeIngredientRow(idx)}><X size={16} color={COLOR.rust} /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLOR.inkSoft }}>Instructions</label>
            <textarea value={form.instructions || ""} onChange={(e) => setField("instructions", e.target.value)} rows={4} placeholder="How to actually make it, step by step" className="w-full mt-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }} />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLOR.inkSoft }}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} placeholder="Quick asides — e.g. double the meatballs, they go fast" className="w-full mt-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink }} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t" style={{ borderColor: COLOR.divider }}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton icon={Check} onClick={save}>Save meal</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   PRINT RECIPES  (checkbox picker + a printer-friendly render of the
   selected recipes, in the same isolate-and-show pattern as the
   grocery list's .print-area — see the global <style> block for the
   .recipe-print-area rules that keep this invisible on screen)
--------------------------------------------------------------- */
/* Generic "pick some recipes, then do X" checkbox picker — backs both the
   Print flow and the Tidy Up flow, which are otherwise identical UI. */
function RecipeSelectModal({ meals, initialSelected, title, actionIcon, actionLabel, onConfirm, onClose }) {
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allIds = meals.map((m) => m.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 no-print" style={{ background: "#00000066" }}>
      {/* max-h caps the column at 85vh without forcing a short list (e.g. 3
          recipes) to render as a mostly-empty tall box. The checklist below
          needs BOTH flex-1 (take the remaining space) and min-h-0 (permit
          shrinking below its content height) for that cap to actually turn
          into a scrollbar instead of the column just growing past 85vh —
          flex items default to min-height:auto, which ignores the parent's
          height cap entirely and pushes Cancel/action off screen. */}
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-lg shadow-xl" style={{ background: COLOR.paper }}>
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: COLOR.divider }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, color: COLOR.ink }}>{title}</h2>
          <button onClick={onClose}><X size={20} color={COLOR.inkSoft} /></button>
        </div>

        <div className="px-5 pt-4 pb-2 flex items-center justify-between shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>
          <p className="text-xs" style={{ color: COLOR.inkSoft }}>{selected.size} of {meals.length} selected</p>
          <button onClick={toggleAll} className="text-xs underline" style={{ color: COLOR.goldDeep }}>{allSelected ? "Deselect all" : "Select all"}</button>
        </div>

        <div className="px-5 pb-4 overflow-y-auto space-y-1 flex-1 min-h-0">
          {meals.map((m) => (
            <label key={m.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded cursor-pointer" style={{ background: selected.has(m.id) ? COLOR.gold + "18" : "transparent" }}>
              <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />
              <span className="text-sm" style={{ color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>{m.name}</span>
              <span className="text-xs uppercase ml-auto" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>{m.category}</span>
            </label>
          ))}
          {meals.length === 0 && <p className="text-sm py-6 text-center" style={{ color: COLOR.inkSoft }}>Your recipe box is empty.</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t shrink-0" style={{ borderColor: COLOR.divider }}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton icon={actionIcon} disabled={selected.size === 0} onClick={() => onConfirm([...selected])}>
            {actionLabel(selected.size)}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

/* Single source of truth for "what a recipe looks like on paper" — used
   both by the hidden .recipe-print-area (what actually prints) and by
   PrintPreviewModal (what you see on screen first). Rendering both from
   the same component is what makes the preview trustworthy: there's no
   second copy of this markup that could drift out of sync with the real
   print output. */
function RecipePrintCard({ meal, pageBreak }) {
  const steps = (meal.instructions || "").split(/\n+/).map((s) => s.trim().replace(/^\d+[.)]\s*/, "")).filter(Boolean);
  const attrs = [meal.attributes?.cuisine, meal.attributes?.protein, meal.attributes?.course].filter(Boolean);
  const body = { fontFamily: "'Inter', sans-serif" };
  return (
    <div style={{ padding: "24px 0", pageBreakAfter: pageBreak ? "always" : "auto" }}>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: "#000", marginBottom: 4 }}>{meal.name}</h2>
      <p style={{ ...body, fontSize: 11, color: "#555", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {meal.category}{attrs.length > 0 ? " · " + attrs.join(" · ") : ""}
      </p>
      {meal.notes && <p style={{ ...body, fontSize: 12, fontStyle: "italic", color: "#333", marginBottom: 12 }}>{meal.notes}</p>}
      {meal.ingredients.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ ...body, fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#000" }}>Ingredients</h3>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {meal.ingredients.map((ing, i) => <li key={i} style={{ ...body, fontSize: 13, color: "#000", marginBottom: 2 }}>{ing.qty} {ing.name}</li>)}
          </ul>
        </div>
      )}
      <div>
        <h3 style={{ ...body, fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#000" }}>Instructions</h3>
        {steps.length > 0 ? (
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {steps.map((s, i) => <li key={i} style={{ ...body, fontSize: 13, color: "#000", marginBottom: 6, lineHeight: 1.5 }}>{s}</li>)}
          </ol>
        ) : <p style={{ ...body, fontSize: 12, color: "#666" }}>No instructions saved.</p>}
      </div>
    </div>
  );
}

function RecipePrintArea({ meals }) {
  if (meals.length === 0) return null;
  return (
    <div className="print-area recipe-print-area">
      {meals.map((m, idx) => <RecipePrintCard key={m.id} meal={m} pageBreak={idx < meals.length - 1} />)}
    </div>
  );
}

/* On-screen preview of exactly what will print — same RecipePrintCard,
   just laid out as a stack of "pages" on a neutral backdrop instead of
   isolated via print CSS. This is what answers "show me before I print
   28 pages": no OS print dialog required to see the result. */
function PrintPreviewModal({ meals, onBack, onPrint }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col no-print" style={{ background: "#00000090" }}>
      <div className="flex items-center justify-between px-5 py-3 border-b shrink-0" style={{ background: COLOR.paper, borderColor: COLOR.divider }}>
        <div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, color: COLOR.ink }}>Print preview</h2>
          <p className="text-xs" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>
            {meals.length} recipe{meals.length === 1 ? "" : "s"} · one page each when printed
          </p>
        </div>
        <div className="flex gap-2">
          <GhostButton onClick={onBack}>Back to selection</GhostButton>
          <PrimaryButton icon={Printer} onClick={onPrint}>Print</PrimaryButton>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6" style={{ background: "#5c5648" }}>
        <div className="mx-auto space-y-6" style={{ maxWidth: 680 }}>
          {meals.map((m) => (
            <div key={m.id} className="shadow-lg" style={{ background: "#fff", padding: "40px 48px", borderRadius: 2 }}>
              <RecipePrintCard meal={m} pageBreak={false} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Shown while cleanRecipesChunk calls are in flight — no cancel button,
   since a chunk already in progress can't be aborted mid-request; closing
   the tab is the only way out, same as any other in-flight save in this
   app. */
function TidyUpProgressOverlay({ done, total }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 no-print" style={{ background: "#00000066" }}>
      <div className="rounded-lg shadow-xl p-8" style={{ background: COLOR.paper }}>
        <div className="flex items-center gap-3">
          <Loader2 size={20} className="animate-spin" color={COLOR.goldDeep} />
          <p className="text-sm" style={{ color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>
            Tidying up recipe {Math.min(done + 1, total)} of {total}…
          </p>
        </div>
      </div>
    </div>
  );
}

/* Before/after review — nothing from cleanRecipesChunk gets saved until the
   cook explicitly approves it here. Defaults to everything checked (the
   common case is "yes, this is better"), but recipes the model couldn't
   parse are excluded and listed separately rather than silently dropped. */
function TidyUpReviewModal({ results, onSave, onClose }) {
  const changed = results.filter((r) => r.cleaned);
  const failed = results.filter((r) => !r.cleaned);
  const [approved, setApproved] = useState(() => new Set(changed.map((r) => r.meal.id)));
  const toggle = (id) => setApproved((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 no-print" style={{ background: "#00000066" }}>
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg shadow-xl" style={{ background: COLOR.paper }}>
        <div className="px-5 py-4 border-b shrink-0" style={{ borderColor: COLOR.divider }}>
          <div className="flex items-center justify-between">
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, color: COLOR.ink }}>Review cleanup</h2>
            <button onClick={onClose}><X size={20} color={COLOR.inkSoft} /></button>
          </div>
          <p className="text-xs mt-1" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>
            Nothing is saved yet — uncheck any recipe you'd rather leave as-is.
          </p>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0 space-y-3">
          {changed.length === 0 && failed.length === 0 && (
            <p className="text-sm text-center py-6" style={{ color: COLOR.inkSoft }}>Nothing came back to review.</p>
          )}
          {changed.map(({ meal, cleaned }) => (
            <div key={meal.id} className="rounded-md border p-3" style={{ borderColor: COLOR.divider }}>
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input type="checkbox" checked={approved.has(meal.id)} onChange={() => toggle(meal.id)} />
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: 15, color: COLOR.ink }}>{meal.name}</span>
              </label>
              <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div>
                  <p className="text-xs uppercase mb-1" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>Before</p>
                  <p className="text-xs whitespace-pre-wrap" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>{meal.instructions || "(no instructions saved)"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1" style={{ color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>After</p>
                  <p className="text-xs whitespace-pre-wrap" style={{ color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>{cleaned.instructions}</p>
                </div>
              </div>
            </div>
          ))}
          {failed.length > 0 && (
            <p className="text-xs" style={{ color: COLOR.rust, fontFamily: "'Inter', sans-serif" }}>
              Couldn't clean up {failed.length} recipe{failed.length === 1 ? "" : "s"}: {failed.map((f) => f.meal.name).join(", ")}. Left untouched.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t shrink-0" style={{ borderColor: COLOR.divider }}>
          <GhostButton onClick={onClose}>Discard</GhostButton>
          <PrimaryButton
            icon={Check}
            disabled={approved.size === 0}
            onClick={() => onSave(changed.filter((r) => approved.has(r.meal.id)).map((r) => ({ ...r.meal, instructions: r.cleaned.instructions, ingredients: r.cleaned.ingredients })))}
          >
            Save {approved.size} recipe{approved.size === 1 ? "" : "s"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   RECIPE BOX VIEW  (now with Cuisine / Protein filters)
--------------------------------------------------------------- */
function RecipeBox({ meals, onEdit, onDelete, onToggleFavorite, onAddNew, onCook, onTidyUpSave }) {
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [catFilter, setCatFilter] = useState("all");
  const [attrFilters, setAttrFilters] = useState({}); // { cuisine: "Italian", protein: "all" }
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [previewMeals, setPreviewMeals] = useState(null);
  const [printMeals, setPrintMeals] = useState([]);
  const [tidyModalOpen, setTidyModalOpen] = useState(false);
  const [tidyProgress, setTidyProgress] = useState(null); // { done, total } while chunks are in flight
  const [tidyResults, setTidyResults] = useState(null); // [{ meal, cleaned, error }] once done

  const runTidyUp = async (selectedIds) => {
    setTidyModalOpen(false);
    const targets = meals.filter((m) => selectedIds.includes(m.id));
    setTidyProgress({ done: 0, total: targets.length });
    const CHUNK_SIZE = 3;
    const results = [];
    for (let i = 0; i < targets.length; i += CHUNK_SIZE) {
      const chunk = targets.slice(i, i + CHUNK_SIZE);
      try {
        const cleanedChunk = await cleanRecipesChunk(chunk);
        chunk.forEach((meal) => {
          const cleaned = cleanedChunk.find((c) => c.id === meal.id);
          results.push({ meal, cleaned: cleaned || null });
        });
      } catch {
        chunk.forEach((meal) => results.push({ meal, cleaned: null }));
      }
      setTidyProgress({ done: Math.min(i + CHUNK_SIZE, targets.length), total: targets.length });
    }
    setTidyProgress(null);
    setTidyResults(results);
  };

  const filtered = useMemo(() => {
    return meals
      .filter((m) => m.name.toLowerCase().includes(query.toLowerCase()))
      .filter((m) => (tagFilter === "all" ? true : m.tags.includes(tagFilter)))
      .filter((m) => (favOnly ? m.favorite : true))
      .filter((m) => (catFilter === "all" ? true : m.category === catFilter))
      .filter((m) => ATTRIBUTE_TYPES.every((type) => {
        const want = attrFilters[type.id];
        return !want || want === "all" ? true : m.attributes?.[type.id] === want;
      }))
      .sort((a, b) => daysAgo(b.lastUsed) - daysAgo(a.lastUsed));
  }, [meals, query, tagFilter, favOnly, catFilter, attrFilters]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" color={COLOR.inkSoft} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search meals..." className="pl-8 pr-3 py-1.5 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }} />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>
          <option value="all">All courses</option>
          <option value="dinner">Dinner</option>
          <option value="side">Side</option>
          <option value="lunch">Lunch</option>
        </select>
        {ATTRIBUTE_TYPES.map((type) => (
          <select key={type.id} value={attrFilters[type.id] || "all"} onChange={(e) => setAttrFilters((f) => ({ ...f, [type.id]: e.target.value }))} className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>
            <option value="all">All {type.label.toLowerCase()}</option>
            {type.values.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        ))}
        <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>
          <option value="all">Everyone</option>
          {FAMILY.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <button onClick={() => setFavOnly((v) => !v)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm" style={{ borderColor: favOnly ? COLOR.gold : COLOR.divider, background: favOnly ? COLOR.gold + "22" : "transparent", color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>
          <Star size={13} fill={favOnly ? COLOR.gold : "none"} color={favOnly ? COLOR.gold : COLOR.inkSoft} /> Favorites
        </button>
        <GhostButton icon={Printer} onClick={() => setPrintModalOpen(true)}>Print recipes</GhostButton>
        <GhostButton icon={Wand2} onClick={() => setTidyModalOpen(true)}>Tidy up recipes</GhostButton>
        <div className="flex-1" />
        <PrimaryButton icon={Plus} onClick={onAddNew}>Add meal</PrimaryButton>
      </div>

      <p className="text-xs mb-3" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>{filtered.length} of {meals.length} meals · sorted by least recently used</p>

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {filtered.map((m, i) => (
          <RecipeCard key={m.id} meal={m} rotate={(i % 3) - 1 ? ((i % 5) - 2) * 0.35 : 0} onEdit={onEdit} onDelete={onDelete} onToggleFavorite={onToggleFavorite} onCook={onCook} />
        ))}
        {filtered.length === 0 && <p className="text-sm col-span-full py-10 text-center" style={{ color: COLOR.inkSoft }}>No meals match. Try clearing a filter, or add a new one.</p>}
      </div>

      {printModalOpen && (
        <RecipeSelectModal
          meals={meals}
          // Only pre-check the currently filtered/searched subset when a
          // filter is actually narrowing things down. With no filter
          // active, filtered === meals, and defaulting to "everything
          // checked" meant clicking Print recipes with a 28-item box
          // silently queued up all 28 pages. Start empty in that case —
          // Select all is one click away if that's genuinely what's wanted.
          initialSelected={filtered.length < meals.length ? filtered.map((m) => m.id) : []}
          title="Print recipes"
          actionIcon={Printer}
          actionLabel={(n) => `Preview ${n} recipe${n === 1 ? "" : "s"}`}
          onClose={() => setPrintModalOpen(false)}
          onConfirm={(selectedIds) => {
            setPreviewMeals(meals.filter((m) => selectedIds.includes(m.id)));
            setPrintModalOpen(false);
          }}
        />
      )}
      {previewMeals && (
        <PrintPreviewModal
          meals={previewMeals}
          onBack={() => { setPrintModalOpen(true); setPreviewMeals(null); }}
          onPrint={() => {
            setPrintMeals(previewMeals);
            setTimeout(() => window.print(), 50);
          }}
        />
      )}
      <RecipePrintArea meals={printMeals} />

      {tidyModalOpen && (
        <RecipeSelectModal
          meals={meals}
          // Same "don't default to everything" reasoning as Print — this
          // one costs an API call per recipe, so an accidental Select-all
          // shouldn't be the default.
          initialSelected={filtered.length < meals.length ? filtered.map((m) => m.id) : []}
          title="Tidy up recipes"
          actionIcon={Wand2}
          actionLabel={(n) => `Tidy up ${n} recipe${n === 1 ? "" : "s"}`}
          onClose={() => setTidyModalOpen(false)}
          onConfirm={runTidyUp}
        />
      )}
      {tidyProgress && <TidyUpProgressOverlay done={tidyProgress.done} total={tidyProgress.total} />}
      {tidyResults && (
        <TidyUpReviewModal
          results={tidyResults}
          onClose={() => setTidyResults(null)}
          onSave={async (updatedMeals) => {
            setTidyResults(null);
            await onTidyUpSave(updatedMeals);
          }}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------
   WEEKLY PLANNER — Dinner Board (corkboard) + Lunch Board (grid)
--------------------------------------------------------------- */
function LunchCell({ items, options, onAdd, onRemove }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="rounded-md p-2 min-h-[64px]" style={{ background: COLOR.paperDim, border: `1px solid ${COLOR.divider}` }}>
      <div className="flex flex-wrap gap-1 mb-1">
        {items.map((id) => {
          const meal = options.find((m) => m.id === id);
          if (!meal) return null;
          return (
            <span key={id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]" style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}`, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>
              {meal.name}
              <button onClick={() => onRemove(id)}><X size={10} color={COLOR.rust} /></button>
            </span>
          );
        })}
      </div>
      {adding ? (
        <select
          autoFocus
          value=""
          onChange={(e) => { onAdd(e.target.value); setAdding(false); }}
          onBlur={() => setAdding(false)}
          className="w-full text-[10px] px-1 py-0.5 rounded border"
          style={{ borderColor: COLOR.divider, color: COLOR.ink }}
        >
          <option value="">pick one…</option>
          {options.filter((m) => !items.includes(m.id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      ) : (
        <button onClick={() => setAdding(true)} className="text-[10px] flex items-center gap-0.5" style={{ color: COLOR.goldDeep }}><Plus size={11} /> add</button>
      )}
    </div>
  );
}

function WeeklyPlanner({ meals, plan, setPlan, onLockIn, onAddGrocery, onRemoveGrocery, weekStart, weekOffset, onPrevWeek, onNextWeek, onJumpToday, isMobile, onCook }) {
  const [board, setBoard] = useState("dinner"); // "dinner" | "lunch"
  // Which days get pulled in when generating a grocery list. Defaults to
  // every day; unchecking a day (e.g. because you already shopped for it,
  // or it's a future week you're not ready to buy for yet) leaves it out.
  // Transient UI state — resets to "all" each time you open the planner,
  // not persisted, since it's a one-shot choice at generation time.
  const [selectedDays, setSelectedDays] = useState(() => [...DAYS]);
  // Mobile-only: which single day the Lunch board is showing. The full
  // "who × day" matrix is a real table on desktop; on a phone that same
  // matrix is the thing Sean specifically flagged as unusable, so mobile
  // gets a day-at-a-time pager instead — see the lunch board render below.
  const todayDayName = DAY_ABBR_FOR_TODAY();
  const [mobileLunchDay, setMobileLunchDay] = useState(DAYS.includes(todayDayName) ? todayDayName : DAYS[0]);
  const dinners = meals.filter((m) => m.category === "dinner");
  const sides = meals.filter((m) => m.category === "side");
  const lunchOptions = meals.filter((m) => m.category === "lunch");

  const setSlot = (day, slot, value) => setPlan((p) => ({ ...p, [day]: { ...p[day], [slot]: value || null } }));

  const setLunch = (day, personId, updater) =>
    setPlan((p) => {
      const daySlot = p[day] || {};
      if (personId === "sean") {
        const current = daySlot.seanLunch || [];
        return { ...p, [day]: { ...daySlot, seanLunch: updater(current) } };
      }
      const current = daySlot.lunches?.[personId] || [];
      return { ...p, [day]: { ...daySlot, lunches: { ...daySlot.lunches, [personId]: updater(current) } } };
    });

  const allDaysSelected = selectedDays.length === DAYS.length;
  const toggleDay = (day) => setSelectedDays((cur) => cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day]);
  const toggleAllDays = () => setSelectedDays(allDaysSelected ? [] : [...DAYS]);
  const isFutureWeek = weekOffset > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button onClick={onPrevWeek} className="p-1 rounded hover:opacity-70" style={{ color: COLOR.inkSoft }} title="Previous week"><ChevronLeft size={16} /></button>
            <p className="text-sm min-w-[130px] text-center" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>
              Week of {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </p>
            <button onClick={onNextWeek} className="p-1 rounded hover:opacity-70" style={{ color: COLOR.inkSoft }} title="Next week"><ChevronRight size={16} /></button>
          </div>
          {weekOffset !== 0 && (
            <button onClick={onJumpToday} className="text-xs underline" style={{ color: COLOR.gold }}>Jump to this week</button>
          )}
          <div className="flex rounded-md overflow-hidden border" style={{ borderColor: COLOR.divider }}>
            <button onClick={() => setBoard("dinner")} className="px-3 py-1.5 text-xs font-medium flex items-center gap-1.5" style={{ background: board === "dinner" ? COLOR.gold : "transparent", color: board === "dinner" ? "#231C10" : COLOR.inkSoft }}><Utensils size={12} /> Dinner Board</button>
            <button onClick={() => setBoard("lunch")} className="px-3 py-1.5 text-xs font-medium flex items-center gap-1.5" style={{ background: board === "lunch" ? COLOR.gold : "transparent", color: board === "lunch" ? "#231C10" : COLOR.inkSoft }}><Sandwich size={12} /> Lunch Board</button>
          </div>
        </div>
        <div className="flex gap-2">
          <GhostButton icon={ShoppingCart} onClick={() => onAddGrocery(selectedDays)}>Add to list</GhostButton>
          <GhostButton icon={X} danger onClick={() => onRemoveGrocery(selectedDays)}>Remove from list</GhostButton>
          <PrimaryButton
            icon={Check}
            onClick={onLockIn}
            disabled={isFutureWeek}
            title={isFutureWeek ? "Lock in isn't available for a future week — it records when meals were actually cooked." : undefined}
          >
            Lock in this week
          </PrimaryButton>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>Grocery days:</span>
        <button onClick={toggleAllDays} className="rounded border" style={{ borderColor: COLOR.divider, color: COLOR.goldDeep, fontSize: isMobile ? 13 : 11, padding: isMobile ? "6px 10px" : "2px 8px" }}>
          {allDaysSelected ? "Deselect all" : "Select all"}
        </button>
        {DAYS.map((day) => {
          const on = selectedDays.includes(day);
          return (
            <button
              key={day}
              onClick={() => toggleDay(day)}
              className="rounded border font-medium"
              style={{ borderColor: on ? COLOR.gold : COLOR.divider, background: on ? COLOR.gold : "transparent", color: on ? "#231C10" : COLOR.inkSoft, fontSize: isMobile ? 13 : 11, padding: isMobile ? "6px 10px" : "2px 8px", minHeight: isMobile ? 36 : undefined, minWidth: isMobile ? 36 : undefined }}
            >
              {day}
            </button>
          );
        })}
      </div>

      {board === "dinner" ? (
        <div className="rounded-xl p-5" style={{ background: `radial-gradient(circle at 1px 1px, #6B573F 1px, transparent 0) 0 0/14px 14px, ${COLOR.cork}` }}>
          <div className="grid gap-4" style={{ gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(150px, 1fr))" }}>
            {DAYS.map((day, i) => {
              const slot = plan[day] || {};
              const dinnerMeal = slot.dinner ? findMeal(meals, slot.dinner) : null;
              const sideMeal = slot.side ? findMeal(meals, slot.side) : null;
              const rotate = isMobile ? 0 : ((i % 3) - 1) * 1.2;
              return (
                <div key={day} className="rounded-md p-3 relative shadow-md" style={{ background: COLOR.card, transform: `rotate(${rotate}deg)` }}>
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full shadow" style={{ background: COLOR.rust }} />
                  <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>{day}</p>
                  <div className="flex items-end gap-1 mb-2">
                    <div className="flex-1">
                      <label className="text-[10px] uppercase" style={{ color: COLOR.inkSoft }}>Dinner</label>
                      <select value={slot.dinner || ""} onChange={(e) => setSlot(day, "dinner", e.target.value)} className="w-full mt-0.5 rounded border" style={{ borderColor: COLOR.divider, color: COLOR.ink, fontSize: isMobile ? 15 : 12, padding: isMobile ? "8px 6px" : "4px 6px" }}>
                        <option value="">— none —</option>
                        {dinners.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                    {dinnerMeal && (
                      <button onClick={() => onCook(dinnerMeal)} title={`Cook ${dinnerMeal.name}`} className="shrink-0 rounded p-1.5" style={{ background: COLOR.gold + "22", color: COLOR.goldDeep, marginBottom: 1 }}>
                        <ChefHat size={isMobile ? 16 : 13} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-end gap-1">
                    <div className="flex-1">
                      <label className="text-[10px] uppercase" style={{ color: COLOR.inkSoft }}>Side</label>
                      <select value={slot.side || ""} onChange={(e) => setSlot(day, "side", e.target.value)} className="w-full mt-0.5 rounded border" style={{ borderColor: COLOR.divider, color: COLOR.ink, fontSize: isMobile ? 15 : 12, padding: isMobile ? "8px 6px" : "4px 6px" }}>
                        <option value="">— none —</option>
                        {sides.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                    {sideMeal && (
                      <button onClick={() => onCook(sideMeal)} title={`Cook ${sideMeal.name}`} className="shrink-0 rounded p-1.5" style={{ background: COLOR.gold + "22", color: COLOR.goldDeep, marginBottom: 1 }}>
                        <ChefHat size={isMobile ? 16 : 13} />
                      </button>
                    )}
                  </div>
                  {dinnerMeal && <div className="flex flex-wrap gap-1 mt-2">{dinnerMeal.tags.slice(0, 6).map((t) => <FamilyDot key={t} id={t} size={14} />)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ) : isMobile ? (
        // Mobile Lunch board: the desktop version is a "who × day" table
        // that's genuinely a matrix (5-6 people by 7 days) — that's the
        // exact thing Sean flagged as unusable on a phone even in landscape.
        // Rather than shrink it, this is a day-at-a-time pager: one day
        // visible, everyone's lunch for that day stacked vertically, same
        // LunchCell control as desktop, just arranged for a thumb not a
        // mouse. Built with plain inline styles, not new Tailwind classes,
        // since there's no matching CSS to introduce new utility classes
        // against safely.
        <div className="rounded-xl p-4" style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <button onClick={() => setMobileLunchDay((d) => DAYS[(DAYS.indexOf(d) + 6) % 7])} style={{ padding: "8px 12px", color: COLOR.inkSoft }}><ChevronLeft size={18} /></button>
            <p style={{ fontSize: 16, fontWeight: 700, color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>{mobileLunchDay}</p>
            <button onClick={() => setMobileLunchDay((d) => DAYS[(DAYS.indexOf(d) + 1) % 7])} style={{ padding: "8px 12px", color: COLOR.inkSoft }}><ChevronRight size={18} /></button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {LUNCH_ROWS.map((personId) => {
              const person = findFamily(personId);
              const items = personId === "sean" ? (plan[mobileLunchDay]?.seanLunch || []) : (plan[mobileLunchDay]?.lunches?.[personId] || []);
              return (
                <div key={personId}>
                  <div className="flex items-center gap-2 mb-1"><FamilyDot id={personId} size={22} /><span className="text-sm" style={{ color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>{person.name}</span></div>
                  <LunchCell
                    items={items}
                    options={lunchOptions}
                    onAdd={(id) => id && setLunch(mobileLunchDay, personId, (cur) => cur.includes(id) ? cur : [...cur, id])}
                    onRemove={(id) => setLunch(mobileLunchDay, personId, (cur) => cur.filter((x) => x !== id))}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-4 overflow-x-auto" style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}` }}>
          <table className="w-full border-collapse min-w-[820px]">
            <thead>
              <tr>
                <th className="text-left p-2 text-xs uppercase" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>Who</th>
                {DAYS.map((d) => <th key={d} className="text-left p-2 text-xs uppercase" style={{ color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {LUNCH_ROWS.map((personId) => {
                const person = findFamily(personId);
                return (
                  <tr key={personId} style={{ borderTop: `1px solid ${COLOR.divider}` }}>
                    <td className="p-2 align-top">
                      <div className="flex items-center gap-2"><FamilyDot id={personId} size={20} /><span className="text-sm" style={{ color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>{person.name}</span></div>
                    </td>
                    {DAYS.map((day) => {
                      const items = personId === "sean" ? (plan[day]?.seanLunch || []) : (plan[day]?.lunches?.[personId] || []);
                      return (
                        <td key={day} className="p-1.5 align-top">
                          <LunchCell
                            items={items}
                            options={lunchOptions}
                            onAdd={(id) => id && setLunch(day, personId, (cur) => cur.includes(id) ? cur : [...cur, id])}
                            onRemove={(id) => setLunch(day, personId, (cur) => cur.filter((x) => x !== id))}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------
   GROCERY LIST VIEW
--------------------------------------------------------------- */
function GroceryList({ items, checked, setChecked, onReset, onClearList, extras, onAddExtra, onRemoveExtra, isMobile }) {
  const [newItemName, setNewItemName] = useState("");
  const [newItemStore, setNewItemStore] = useState("Target");

  const groups = useMemo(() => {
    const g = {};
    items.forEach((item) => {
      const store = STORE_FOR(item.category);
      if (!g[store]) g[store] = [];
      g[store].push({ type: "meal", key: item.name + "|" + item.category, name: item.name, subtitle: combineQuantities(item.entries.map((e) => e.qty)) });
    });
    extras.forEach((item) => {
      if (!g[item.store]) g[item.store] = [];
      g[item.store].push({ type: "extra", key: "extra:" + item.id, id: item.id, name: item.name, subtitle: "added manually" });
    });
    return g;
  }, [items, extras]);

  const toggle = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));
  const total = items.length + extras.length;
  const done = items.filter((i) => checked[i.name + "|" + i.category]).length + extras.filter((i) => checked["extra:" + i.id]).length;

  const addItem = () => {
    if (!newItemName.trim()) return;
    onAddExtra(newItemName.trim(), newItemStore);
    setNewItemName("");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 no-print flex-wrap gap-3">
        <p className="text-sm" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>{done} of {total} checked off</p>
        <div className="flex gap-2 flex-wrap">
          <GhostButton icon={RotateCcw} onClick={onReset}>Reset checklist</GhostButton>
          <GhostButton icon={X} danger onClick={() => { if (window.confirm("Clear the whole grocery list? This removes every meal-derived item — manually added extras stay.")) onClearList(); }}>Clear list</GhostButton>
          <GhostButton icon={Printer} onClick={() => window.print()}>Print list</GhostButton>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5 no-print flex-wrap">
        <input
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addItem()}
          placeholder="Add an item — toilet paper, diapers, etc."
          className="flex-1 rounded-md border text-sm"
          style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif", padding: isMobile ? "10px 12px" : "8px 12px", minWidth: isMobile ? "100%" : undefined }}
        />
        <select value={newItemStore} onChange={(e) => setNewItemStore(e.target.value)} className="rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif", padding: isMobile ? "10px 8px" : "8px" }}>
          <option value="Target">Target</option>
          <option value="Harris Teeter">Harris Teeter</option>
        </select>
        <PrimaryButton icon={Plus} onClick={addItem}>Add</PrimaryButton>
      </div>

      {total === 0 && <p className="text-sm py-10 text-center" style={{ color: COLOR.inkSoft }}>Nothing on the list yet. Add days from the Weekly Board, or add an item above.</p>}

      <div className="print-area">
        <h2 className="hidden print-only mb-4" style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "#000" }}>Grocery List</h2>
        <div className="grid gap-6" style={{ gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))" }}>
          {Object.entries(groups).map(([store, storeItems]) => (
            <div key={store} className="rounded-lg p-4 print-store" style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}` }}>
              <h3 className="mb-3" style={{ fontFamily: "'Fraunces', serif", fontSize: 18, color: COLOR.ink }}>{store}</h3>
              <div className="space-y-2">
                {storeItems.map((item) => {
                  const isChecked = !!checked[item.key];
                  return (
                    <div key={item.key} className="flex items-start gap-2.5">
                      <label className="flex items-start gap-2.5 cursor-pointer flex-1" style={{ minHeight: isMobile ? 36 : undefined }}>
                        <input type="checkbox" checked={isChecked} onChange={() => toggle(item.key)} className="mt-1" style={isMobile ? { width: 20, height: 20 } : undefined} />
                        <div>
                          <p className="text-sm" style={{ color: isChecked ? COLOR.inkSoft : COLOR.ink, textDecoration: isChecked ? "line-through" : "none", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? 15 : undefined }}>{item.name}</p>
                          <p className="text-xs" style={{ color: COLOR.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>{item.subtitle}</p>
                        </div>
                      </label>
                      {item.type === "extra" && (
                        <button onClick={() => onRemoveExtra(item.id)} className="no-print mt-0.5" title="Remove permanently" style={isMobile ? { padding: 8 } : undefined}>
                          <X size={isMobile ? 18 : 14} color={COLOR.rust} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   MOCK AI RESPONSES
   In the real build these come from anthropic-proxy-worker with a
   forced-JSON prompt (same trick as the News feed). Here they're
   canned so you can see the interaction shape before that's wired up.
--------------------------------------------------------------- */
const WORKER_URL = "https://anthropic-proxy-worker.seancprince.workers.dev";

// Drives every mobile layout decision in this file via JS rather than CSS
// breakpoints — this project has no Tailwind build (see the CRACO/Tailwind
// note elsewhere), just a hand-matched plain CSS file, so introducing new
// classNames like `md:hidden` would silently do nothing. This sidesteps
// that entirely: components read `isMobile` and branch their own inline
// styles/markup, no CSS file dependency for anything new.
// Checks BOTH viewport width and touch capability, not width alone — a
// setting like Safari's "Request Desktop Website" (Settings > Safari >
// Request Desktop Website > All Websites) makes a real phone report a
// virtual desktop-sized viewport, which a width-only check would wrongly
// read as "desktop." Requiring touch support too catches that case.
function useIsMobile() {
  const evaluate = () => {
    if (typeof window === "undefined") return false;
    const narrow = window.matchMedia("(max-width: 768px)").matches;
    const isTouchDevice = navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches;
    const narrowish = window.matchMedia("(max-width: 900px)").matches;
    return narrow || (narrowish && isTouchDevice);
  };
  const [isMobile, setIsMobile] = useState(evaluate);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const mq2 = window.matchMedia("(max-width: 900px)");
    const onChange = () => setIsMobile(evaluate());
    mq.addEventListener("change", onChange);
    mq2.addEventListener("change", onChange);
    return () => { mq.removeEventListener("change", onChange); mq2.removeEventListener("change", onChange); };
  }, []);
  return isMobile;
}

function collectResponseText(data) {
  return (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
}

// The proxy response sometimes has prose before or around the JSON block,
// so this scans for the first balanced {...} or [...] rather than trusting
// the whole text to be clean JSON. Same lesson learned from the News feed.
function extractJSON(rawText) {
  try { return JSON.parse(rawText); } catch {}
  const arrMatch = rawText.match(/\[[\s\S]*\]/);
  if (arrMatch) { try { return JSON.parse(arrMatch[0]); } catch {} }
  const objMatch = rawText.match(/\{[\s\S]*\}/);
  if (objMatch) { try { return JSON.parse(objMatch[0]); } catch {} }
  return null;
}

async function callWorker(messages, maxTokens = 1500) {
  const res = await fetch(WORKER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: maxTokens, messages }),
  });
  if (!res.ok) throw new Error(`Worker returned ${res.status}`);
  const data = await res.json();
  return collectResponseText(data);
}

// Rewrites a small batch of recipes' instructions into clean discrete steps
// and normalizes ingredient formatting — same worker + JSON-extraction
// pattern as the photo-scan feature. Explicitly told not to touch amounts
// or invent/drop steps, since this is food a family actually cooks from;
// the caller still shows a before/after for the cook to approve, this is
// just the second line of defense.
async function cleanRecipesChunk(meals) {
  const payload = meals.map((m) => ({ id: m.id, name: m.name, instructions: m.instructions || "", ingredients: m.ingredients }));
  const prompt = `Clean up the following recipes for a family recipe box that gets printed and cooked from.

For each recipe:
- Rewrite "instructions" as clear, discrete steps, one action per line (plain text, no leading numbers — the app numbers them for display). Split run-on paragraphs into separate steps, fix garbled OCR text and stray punctuation. Do NOT invent, remove, reorder, or change the substance of any step — only reformat wording and structure.
- Normalize "ingredients": consistent quantity formatting (e.g. "2 lb" not "2lbs" or "two pounds"), and a sensible "category" from produce, protein, dairy, or pantry. Do NOT add, remove, merge, or change the amount of any ingredient — only reformat.
- If a recipe is already clean, return it close to unchanged.

Recipes:
${JSON.stringify(payload)}

Respond with ONLY a JSON array, no prose, no markdown fences, one entry per recipe in the same order, in exactly this shape:
[{"id":"...","instructions":"step one\\nstep two\\n...","ingredients":[{"name":"...","qty":"...","category":"produce|protein|dairy|pantry"}]}]`;
  const text = await callWorker([{ role: "user", content: prompt }], 3000);
  const parsed = extractJSON(text);
  if (!parsed || !Array.isArray(parsed)) throw new Error("couldn't parse a cleaned-up response");
  return parsed;
}

function buildAIContext(meals, plan) {
  const mealLines = meals.map((m) => {
    const tagNames = m.tags.map((t) => findFamily(t)?.name).filter(Boolean).join(", ");
    const attrs = [m.attributes?.cuisine, m.attributes?.protein, m.attributes?.course].filter(Boolean).join(", ");
    return `- "${m.id}": ${m.name} [${m.category}]${attrs ? " (" + attrs + ")" : ""} — liked by: ${tagNames || "no one tagged"}; last used: ${m.lastUsed || "never"}`;
  }).join("\n");
  const planLines = DAYS.map((d) => {
    const slot = plan[d];
    const dinner = slot?.dinner ? findMeal(meals, slot.dinner)?.name : "unplanned";
    const side = slot?.side ? findMeal(meals, slot.side)?.name : "none";
    return `${d}: ${dinner} (side: ${side})`;
  }).join("\n");

  return `You are helping a family of ten plan weekly dinners. Household: two parents (Sean, Brenna) and seven kids ages 1, 3, 5, 8, 11, 13, 15. All kids are picky eaters with minimal vegetable tolerance. Produce is bought at Harris Teeter, most everything else at Target.

Existing meal catalog (id: name [category] (cuisine, protein, course) — liked by; last used):
${mealLines}

Current weekly plan:
${planLines}`;
}

// Resizes and compresses a photo client-side before sending, so a full-res
// phone photo doesn't turn into a multi-megabyte request.
function resizeAndEncodeImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        // 1800/0.92 (up from 1400/0.82) — a photo of a recipe card holds up fine
        // at the old settings, but a browser screenshot with small dense text
        // (ingredient lines, fractions) was going soft enough to blur past
        // legibility. This costs a somewhat larger upload, still well under
        // any request-size concern for a single image.
        const maxDim = 1800;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        resolve({ mediaType: "image/jpeg", data: dataUrl.split(",")[1] });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const ATTRIBUTE_HINT = ATTRIBUTE_TYPES.map((t) => `${t.label}: ${t.values.join(", ")}`).join("\n");

function AskAI({ meals, plan, onApplyPlan, onAddMealToBox, onScannedMeal }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposedPlan, setProposedPlan] = useState(null);
  const [proposedMeals, setProposedMeals] = useState(null);
  const [addedIds, setAddedIds] = useState([]);
  const [sideMealId, setSideMealId] = useState(meals.find((m) => m.category === "dinner")?.id || "");
  const fileInputRef = React.useRef(null);
  const messagesEndRef = React.useRef(null);

  // Auto-scroll to the latest message, same as Command Center's Goals Coach.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  const draftWeek = async () => {
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: "Draft a balanced dinner plan for next week." }]);
    try {
      const prompt = `${buildAIContext(meals, plan)}

Draft a dinner (and optional side) plan for next week, Monday through Sunday. Weight toward meals that haven't been used recently, and vary cuisines and proteins across the week. Use only meal ids that exist in the catalog above — dinner slots need a "dinner"-category id, side slots need a "side"-category id or null.

Respond with ONLY a JSON object, no prose, no markdown fences, in exactly this shape:
{"Mon":{"dinner":"spaghetti","side":"side-garlic-bread"},"Tue":{"dinner":"tacos-beef","side":null}, ... "Sun":{"dinner":"...","side":null}}`;
      const text = await callWorker([{ role: "user", content: prompt }]);
      const parsed = extractJSON(text);
      if (!parsed) throw new Error("couldn't parse a plan from the response");
      setMessages((m) => [...m, { role: "assistant", text: "Here's a draft for next week, weighted toward meals that haven't been on the table recently. Review it below, then apply it to the board if it looks right." }]);
      setProposedPlan(parsed);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong drafting a plan: " + err.message }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestNewMeals = async () => {
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: "Suggest 3 new dinner ideas we haven't tried." }]);
    try {
      const prompt = `${buildAIContext(meals, plan)}

Suggest 3 new dinner ideas we haven't tried, that would realistically work for picky kids (mild flavors, easy to serve components separately rather than mixed together).

Respond with ONLY a JSON array, no prose, no markdown fences, in exactly this shape:
[{"name":"Honey Garlic Chicken Bowls","attributes":{"cuisine":"Asian","protein":"Chicken","course":"Main"},"notes":"short aside about why it'd work for picky eaters","instructions":"step by step method","ingredients":[{"name":"Chicken thighs","qty":"2 lb","category":"protein"}]}]

Use only these allowed values for attributes:
${ATTRIBUTE_HINT}
Ingredient "category" must be one of: produce, protein, dairy, pantry.`;
      const text = await callWorker([{ role: "user", content: prompt }], 2000);
      const parsed = extractJSON(text);
      if (!parsed) throw new Error("couldn't parse meal ideas from the response");
      setMessages((m) => [...m, { role: "assistant", text: "Three ideas that fit the picky-eater constraints. Add any of these straight to the Recipe Box." }]);
      setProposedMeals(parsed);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong finding new ideas: " + err.message }]);
    } finally {
      setLoading(false);
    }
  };

  const send = async (question) => {
    if (!question.trim() || loading) return;
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    try {
      const history = messages.map((m) => `${m.role === "user" ? "Parent" : "Assistant"}: ${m.text}`).join("\n");
      const prompt = `${buildAIContext(meals, plan)}\n\n${history ? "Earlier in this chat:\n" + history + "\n\n" : ""}Parent: ${question}\n\nAnswer as the assistant, directly and practically. This is a chat answer, not a structured action, so plain text is fine here.`;
      const text = await callWorker([{ role: "user", content: prompt }], 800);
      setMessages((m) => [...m, { role: "assistant", text: text || "No response text came back." }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong reaching the assistant: " + err.message }]);
    } finally {
      setLoading(false);
    }
  };

  const scanPhoto = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: files.length > 1 ? `Scanning ${files.length} photos: ${files.map((f) => f.name).join(", ")}` : `Scanning photo: ${files[0].name}` }]);
    try {
      const encoded = await Promise.all(files.map(resizeAndEncodeImage));
      const prompt = `Transcribe this recipe into JSON.${files.length > 1 ? " These are multiple photos of the SAME recipe (e.g. front and back of a card, or multiple pages) — read all of them together and combine into one single recipe, don't treat them as separate meals." : ""} If any photo is a screenshot of a webpage rather than a physical recipe card, ignore navigation bars, ads, popups, and any browser or site chrome, and read only the actual recipe content. Read the ingredients with their quantities, and the full step-by-step instructions. Also make your best guess at Cuisine, Main Protein, and Course from these allowed values only:
${ATTRIBUTE_HINT}

Respond with ONLY a JSON object, no prose, no markdown fences, in exactly this shape:
{"name":"...","instructions":"step by step method","notes":"","attributes":{"cuisine":"...","protein":"...","course":"..."},"ingredients":[{"name":"...","qty":"...","category":"produce|protein|dairy|pantry"}]}`;
      const messagePayload = [{
        role: "user",
        content: [
          ...encoded.map(({ mediaType, data }) => ({ type: "image", source: { type: "base64", media_type: mediaType, data } })),
          { type: "text", text: prompt },
        ],
      }];
      const text = await callWorker(messagePayload, 1500);
      const parsed = extractJSON(text);
      if (!parsed) throw new Error("couldn't read a recipe from that photo");
      setMessages((m) => [...m, { role: "assistant", text: `Read "${parsed.name}" off the photo${files.length > 1 ? "s" : ""}. Cuisine, protein, and course are guesses, everything's editable before you save it.` }]);
      onScannedMeal(parsed);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Couldn't read that photo: " + err.message }]);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const applyPlan = () => { onApplyPlan(proposedPlan); setProposedPlan(null); };
  const addMeal = (idea, idx) => { onAddMealToBox(idea); setAddedIds((a) => [...a, idx]); };

  return (
    <div className="max-w-2xl">
      <div className="flex flex-wrap gap-2 mb-4">
        <GhostButton icon={Wand2} onClick={draftWeek}>Draft next week's plan</GhostButton>
        <GhostButton icon={Sparkles} onClick={suggestNewMeals}>New meal ideas</GhostButton>
        <GhostButton icon={ChefHat} onClick={() => fileInputRef.current?.click()}>Scan a recipe (front/back OK)</GhostButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => scanPhoto(e.target.files)}
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <select value={sideMealId} onChange={(e) => setSideMealId(e.target.value)} className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>
          {meals.filter((m) => m.category === "dinner").map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <GhostButton icon={Salad} onClick={() => send(`What side dishes would pair well with ${findMeal(meals, sideMealId)?.name}?`)}>Suggest sides for this</GhostButton>
      </div>

      <div className="rounded-lg p-4 mb-3 space-y-3 overflow-y-auto" style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}`, minHeight: 200, maxHeight: 320 }}>
        {messages.length === 0 && <p className="text-sm" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>Ask for a weekly plan, a side dish, or a brand-new meal idea. The assistant sees your full recipe box and this week's board.</p>}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
            <div className="inline-block px-3 py-2 rounded-lg text-sm text-left max-w-[85%] whitespace-pre-wrap" style={{ background: m.role === "user" ? COLOR.gold + "33" : COLOR.paperDim, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }}>{m.text}</div>
          </div>
        ))}
        {loading && <div className="flex items-center gap-2 text-sm" style={{ color: COLOR.inkSoft }}><Loader2 size={14} className="animate-spin" /> Thinking...</div>}
        <div ref={messagesEndRef} />
      </div>

      {/* Structured action: draft plan preview + apply */}
      {proposedPlan && (
        <div className="rounded-lg p-4 mb-3" style={{ background: COLOR.paperDim, border: `1px solid ${COLOR.gold}` }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5" style={{ color: COLOR.goldDeep }}><Wand2 size={13} /> Proposed week</p>
            <PrimaryButton icon={ArrowRight} onClick={applyPlan}>Apply to board</PrimaryButton>
          </div>
          <div className="grid grid-cols-7 gap-2 text-xs">
            {DAYS.map((d) => (
              <div key={d} className="rounded p-2" style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}` }}>
                <p className="font-bold mb-1" style={{ color: COLOR.goldDeep, fontFamily: "'IBM Plex Mono', monospace" }}>{d}</p>
                <p style={{ color: COLOR.ink }}>{findMeal(meals, proposedPlan[d].dinner)?.name}</p>
                {proposedPlan[d].side && <p style={{ color: COLOR.inkSoft }}>+ {findMeal(meals, proposedPlan[d].side)?.name}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Structured action: new meal ideas + add to box */}
      {proposedMeals && (
        <div className="rounded-lg p-4 mb-3 space-y-2" style={{ background: COLOR.paperDim, border: `1px solid ${COLOR.teal}` }}>
          <p className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-2" style={{ color: COLOR.teal }}><Sparkles size={13} /> New ideas</p>
          {proposedMeals.map((idea, idx) => (
            <div key={idx} className="flex items-center justify-between gap-3 rounded p-2.5" style={{ background: COLOR.card, border: `1px solid ${COLOR.divider}` }}>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: COLOR.ink, fontFamily: "'Fraunces', serif" }}>{idea.name}</p>
                <p className="text-xs truncate" style={{ color: COLOR.inkSoft }}>{idea.attributes.cuisine} · {idea.attributes.protein} · {idea.notes}</p>
              </div>
              {addedIds.includes(idx)
                ? <Pill color={COLOR.sage}><Check size={11} /> Added</Pill>
                : <GhostButton icon={Plus} onClick={() => addMeal(idea, idx)}>Add to Recipe Box</GhostButton>}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(input)} placeholder="Ask anything about this week's dinners..." className="flex-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: COLOR.divider, background: COLOR.card, color: COLOR.ink, fontFamily: "'Inter', sans-serif" }} />
        <PrimaryButton icon={Send} onClick={() => send(input)}>Send</PrimaryButton>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   APP SHELL
--------------------------------------------------------------- */
export default function MealPlanner() {
  const isMobile = useIsMobile();
  const [meals, setMeals] = useState([]);
  const [plan, setPlan] = useState({});
  // The grocery list is now its own persistent thing, independent of any
  // one week — see the effects below. groceryEntries is a flat list of raw
  // contributions (one entry per meal per source day; see buildDayEntries),
  // not pre-aggregated — computeGroceryList groups them for display.
  const [groceryEntries, setGroceryEntries] = useState([]);
  const [checked, setChecked] = useState({});
  const [extras, setExtras] = useState([]);
  const [view, setView] = useState("recipes");
  const [modalMeal, setModalMeal] = useState(null);
  const [cookMeal, setCookMeal] = useState(null);
  const [toast, setToast] = useState(null);
  const [loadedWeekKey, setLoadedWeekKey] = useState(null); // which week's plan is actually in state right now
  const [globalLoaded, setGlobalLoaded] = useState(false); // whether meals/grocery-list/extras have loaded once
  // weekOffset: 0 = current week, +1 = next week, -1 = last week, etc. Lets
  // Sean build out future weeks without waiting for the current one to pass.
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(() => {
    const d = mondayOfThisWeek();
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [weekOffset]);
  const weekKey = useMemo(() => weekStart.toISOString().slice(0, 10), [weekStart]);
  const saveTimer = React.useRef(null);
  const grocerySaveTimer = React.useRef(null);

  const flash = (text) => { setToast(text); setTimeout(() => setToast(null), 2600); };

  // Load the meal catalog, the grocery list, and manual extras ONCE on
  // mount — all three are global now, not tied to whichever week you
  // happen to be viewing. This used to live inside the per-week effect
  // below and silently re-fetch on every week-navigation click; harmless
  // for meals/extras, but the grocery list previously WAS scoped to the
  // week, which is exactly the "multiple shopping lists" problem this
  // rework fixes — there's one grocery-list document now, full stop.
  useEffect(() => {
    (async () => {
      try {
        const mealsRes = await fetch(`${WORKER_URL}/meals`);
        setMeals(mealsRes.ok ? await mealsRes.json() : []);
      } catch { setMeals([]); flash("Couldn't reach the meal catalog — check your connection."); }

      try {
        const groceryRes = await fetch(`${WORKER_URL}/sync/grocery-list`);
        const grocery = groceryRes.ok ? await groceryRes.json() : null;
        setGroceryEntries(grocery?.entries || []);
        setChecked(grocery?.checked || {});
      } catch { /* leave empty; worth a retry-on-reload if it happened */ }

      try {
        const extrasRes = await fetch(`${WORKER_URL}/sync/grocery-extras`);
        const extrasData = extrasRes.ok ? await extrasRes.json() : null;
        setExtras(extrasData || []);
      } catch { setExtras([]); }

      setGlobalLoaded(true);
    })();
  }, []);

  // Load just this week's plan, on mount and again whenever weekKey changes
  // (i.e. the person navigates to a different week). Setting loadedWeekKey
  // back to null immediately guards the autosave effect below from firing
  // with the PREVIOUS week's still-in-memory plan under the NEW week's key
  // while this fetch is in flight.
  useEffect(() => {
    setLoadedWeekKey(null);
    (async () => {
      try {
        const weekRes = await fetch(`${WORKER_URL}/meal-plan/${weekKey}`);
        const week = weekRes.ok ? await weekRes.json() : null;
        setPlan(week?.plan || {});
      } catch { /* leave the week empty, worth a retry-on-reload if it happened */ }
      setLoadedWeekKey(weekKey);
    })();
  }, [weekKey]);

  // Autosave this week's plan a moment after each change. No longer carries
  // checked/groceryPlan — those moved to the grocery-list effect below.
  useEffect(() => {
    if (loadedWeekKey !== weekKey) return; // don't push stale/previous-week state over this week's real data mid-fetch
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`${WORKER_URL}/meal-plan/${weekKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      }).catch(() => flash("Couldn't sync this week's board — will retry on next change."));
    }, 500);
    return () => clearTimeout(saveTimer.current);
  }, [plan, loadedWeekKey, weekKey]);

  // Autosave the grocery list itself — entries and checklist — independent
  // of which week is on screen. This is what makes it a single persistent
  // list rather than one per week.
  useEffect(() => {
    if (!globalLoaded) return; // don't push an empty list over real data before the initial fetch resolves
    clearTimeout(grocerySaveTimer.current);
    grocerySaveTimer.current = setTimeout(() => {
      fetch(`${WORKER_URL}/sync/grocery-list`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entries: groceryEntries, checked }),
      }).catch(() => flash("Couldn't sync the grocery list — will retry on next change."));
    }, 500);
    return () => clearTimeout(grocerySaveTimer.current);
  }, [groceryEntries, checked, globalLoaded]);

  const saveMeal = async (meal) => {
    const isNew = !meals.some((m) => m.id === meal.id);
    try {
      const res = await fetch(`${WORKER_URL}/meals${isNew ? "" : "/" + meal.id}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(meal),
      });
      if (!res.ok) throw new Error(`save failed (${res.status})`);
      const saved = await res.json();
      setMeals((prev) => (isNew ? [...prev, saved] : prev.map((m) => (m.id === saved.id ? saved : m))));
      setModalMeal(null);
      flash(`Saved "${saved.name}".`);
    } catch (err) {
      flash(`Couldn't save "${meal.name}": ${err.message}`);
    }
  };

  const deleteMeal = async (id) => {
    const meal = findMeal(meals, id);
    try {
      const res = await fetch(`${WORKER_URL}/meals/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`delete failed (${res.status})`);
      setMeals((prev) => prev.filter((m) => m.id !== id));
      flash(`Deleted "${meal?.name}".`);
    } catch (err) {
      flash(`Couldn't delete "${meal?.name}": ${err.message}`);
    }
  };

  const toggleFavorite = (id) => {
    const meal = findMeal(meals, id);
    if (meal) saveMealSilently({ ...meal, favorite: !meal.favorite });
  };

  // Same PUT as saveMeal but skips the modal-close/toast, for quiet toggles
  // like favoriting that shouldn't feel like a full "save" action.
  const saveMealSilently = async (meal) => {
    setMeals((prev) => prev.map((m) => (m.id === meal.id ? meal : m))); // optimistic
    try {
      const res = await fetch(`${WORKER_URL}/meals/${meal.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(meal),
      });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setMeals((prev) => prev.map((m) => (m.id === saved.id ? saved : m)));
    } catch {
      flash(`Couldn't update "${meal.name}" — try again.`);
    }
  };

  // Saves a batch of already-approved recipe edits (used by Tidy Up's
  // review step) — one PUT per recipe, same as lockInWeek's multi-save,
  // but reports how many actually made it rather than an all-or-nothing
  // failure, since a partial batch succeeding is still useful.
  const bulkUpdateMeals = async (updatedMeals) => {
    const results = await Promise.allSettled(updatedMeals.map((m) => fetch(`${WORKER_URL}/meals/${m.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(m),
    }).then((r) => { if (!r.ok) throw new Error(); return r.json(); })));
    const saved = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    if (saved.length) setMeals((prev) => prev.map((m) => saved.find((s) => s.id === m.id) || m));
    const failedCount = results.length - saved.length;
    flash(failedCount
      ? `Tidied up ${saved.length} recipe${saved.length === 1 ? "" : "s"}, but ${failedCount} couldn't save — try again.`
      : `Tidied up ${saved.length} recipe${saved.length === 1 ? "" : "s"}.`);
  };

  const lockInWeek = async () => {
    const monday = weekStart;
    const changed = [];
    const updatedMeals = meals.map((m) => {
      let usedOn = null;
      DAYS.forEach((day, i) => {
        const slot = plan[day];
        const usedThisMeal = slot?.dinner === m.id || slot?.side === m.id || (slot?.seanLunch || []).includes(m.id) || Object.values(slot?.lunches || {}).some((arr) => arr.includes(m.id));
        if (usedThisMeal) { const d = new Date(monday); d.setDate(d.getDate() + i); usedOn = d.toISOString().slice(0, 10); }
      });
      if (usedOn) { const next = { ...m, lastUsed: usedOn }; changed.push(next); return next; }
      return m;
    });
    setMeals(updatedMeals);
    try {
      await Promise.all(changed.map((m) => fetch(`${WORKER_URL}/meals/${m.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(m),
      })));
      flash("This week's plan is locked in — last-used dates updated.");
    } catch {
      flash("Locked in locally, but syncing last-used dates failed — try again in a bit.");
    }
  };

  // "Add" upserts by day: any existing entries tagged with this week+day get
  // stripped first, then replaced with whatever's on the board for that day
  // right now. That's what makes "I changed Thursday's dinner" a one-click
  // fix — just re-select Thursday and hit Add again, no separate remove step
  // needed unless you actually want the day off the list entirely.
  const addDaysToGroceryList = (selectedDays) => {
    const days = (selectedDays && selectedDays.length) ? selectedDays : DAYS;
    const sourceKeysTouched = new Set(days.map((d) => `${weekKey}:${d}`));
    const fresh = days.flatMap((d) => buildDayEntries(weekKey, d, plan[d] || {}));
    setGroceryEntries((cur) => [...cur.filter((e) => !sourceKeysTouched.has(e.sourceKey)), ...fresh]);
    setView("grocery");
    flash(days.length < DAYS.length
      ? `Added ${days.length} day${days.length === 1 ? "" : "s"} to the grocery list.`
      : "Added this week's board to the grocery list.");
  };

  // Strips selected days' contributions out entirely — for when a day's
  // coming off the list, not being replaced by something else.
  const removeDaysFromGroceryList = (selectedDays) => {
    const days = (selectedDays && selectedDays.length) ? selectedDays : DAYS;
    const sourceKeysTouched = new Set(days.map((d) => `${weekKey}:${d}`));
    setGroceryEntries((cur) => cur.filter((e) => !sourceKeysTouched.has(e.sourceKey)));
    flash(`Removed ${days.length} day${days.length === 1 ? "" : "s"} from the grocery list.`);
  };

  // Full reset between shopping trips — clears meal-derived entries and the
  // checklist, but leaves manually-added extras alone (those have their own
  // lifecycle: checked state resets weekly, the item itself persists until
  // explicitly removed — same as before this rework).
  const clearGroceryList = () => {
    setGroceryEntries([]);
    setChecked({});
    flash("Grocery list cleared.");
  };

  const pushExtras = (next) => {
    fetch(`${WORKER_URL}/sync/grocery-extras`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(next),
    }).catch(() => flash("Couldn't sync the shopping list — will retry on next change."));
  };
  const addExtra = (name, store) => {
    const next = [...extras, { id: "extra-" + Date.now(), name, store }];
    setExtras(next);
    pushExtras(next);
  };
  const removeExtra = (id) => {
    const next = extras.filter((e) => e.id !== id);
    setExtras(next);
    pushExtras(next);
  };

  const applyAIPlan = (proposed) => { setPlan((p) => ({ ...p, ...proposed })); flash("Applied the AI's draft to the Dinner Board."); };

  const addAIMealToBox = async (idea) => {
    const meal = { ...idea, id: "m-" + Date.now() + "-" + Math.round(Math.random() * 999), favorite: false, lastUsed: null, tags: [] };
    try {
      const res = await fetch(`${WORKER_URL}/meals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(meal),
      });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setMeals((prev) => [...prev, saved]);
      flash(`Added "${idea.name}" to the Recipe Box.`);
    } catch {
      flash(`Couldn't save "${idea.name}" — try again.`);
    }
  };

  // Photo scan reads are less reliable than structured text suggestions
  // (handwriting, faded print, ambiguous quantities), so this always opens
  // the edit modal for a look rather than adding it directly.
  const handleScannedMeal = (parsed) => {
    setModalMeal({
      prefill: {
        id: "m-" + Date.now(),
        name: parsed.name || "",
        category: "dinner",
        favorite: false,
        lastUsed: null,
        notes: parsed.notes || "",
        instructions: parsed.instructions || "",
        attributes: parsed.attributes || {},
        tags: [],
        ingredients: parsed.ingredients || [],
      },
    });
  };

  const groceryItems = useMemo(() => computeGroceryList(groceryEntries, meals), [groceryEntries, meals]);

  const NAV = [
    { id: "recipes", label: "Recipe Box", icon: ChefHat },
    { id: "planner", label: "Weekly Board", icon: CalendarDays },
    { id: "grocery", label: "Grocery List", icon: ShoppingCart },
    { id: "ai", label: "Ask AI", icon: Sparkles },
  ];
  const titles = { recipes: "Recipe Box", planner: "Weekly Board", grocery: "Grocery List", ai: "Ask AI" };
  const subtitles = {
    recipes: "Every meal in rotation, tagged by cuisine, protein, and who eats it.",
    planner: "Pin dinners and sides to the week, or switch to the Lunch Board.",
    grocery: "One running list — add or remove days from any week's board, plus anything added manually.",
    ai: "Ask for a plan, a side dish, or a new idea — apply it straight to the board.",
  };

  return (
    <div className="flex w-full overflow-hidden" style={{ height: "100vh", background: COLOR.paper, flexDirection: isMobile ? "column" : "row" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .print-only { display: none; }
        .recipe-print-area { display: none; }
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .print-only { display: block !important; }
          .print-store { break-inside: avoid; border: 1px solid #ccc !important; }
          .no-print { display: none !important; }
          .recipe-print-area { display: block !important; }
        }
      `}</style>

      {!isMobile && (
        <aside className="w-56 shrink-0 flex flex-col justify-between p-5 no-print" style={{ background: COLOR.rail }}>
          <div>
            <div className="mb-8">
              <p style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "#F5EFDF", lineHeight: 1.1 }}>The Prince<br />Kitchen</p>
              <p className="text-xs mt-1" style={{ color: "#A79A82", fontFamily: "'IBM Plex Mono', monospace" }}>family of ten · meal command center</p>
            </div>
            <nav className="space-y-1">
              {NAV.map((n) => {
                const active = view === n.id; const Icon = n.icon;
                return (
                  <button key={n.id} onClick={() => setView(n.id)} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors" style={{ background: active ? COLOR.railSoft : "transparent", color: active ? COLOR.gold : "#C9BCA3", fontFamily: "'Inter', sans-serif", fontWeight: active ? 600 : 500 }}>
                    <Icon size={16} /> {n.label}
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="flex flex-wrap gap-1.5">{FAMILY.map((f) => <FamilyDot key={f.id} id={f.id} size={16} />)}</div>
        </aside>
      )}

      <main className="flex-1 overflow-y-auto" style={{ padding: isMobile ? "16px 16px 88px" : 32 }}>
        <div className="mb-6 no-print">
          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: isMobile ? 22 : 28, color: COLOR.ink }}>{titles[view]}</h1>
          <p className="text-sm mt-1" style={{ color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif" }}>{subtitles[view]}</p>
        </div>

        {view === "recipes" && <RecipeBox meals={meals} onEdit={setModalMeal} onDelete={deleteMeal} onToggleFavorite={toggleFavorite} onAddNew={() => setModalMeal("new")} onCook={setCookMeal} onTidyUpSave={bulkUpdateMeals} />}
        {view === "planner" && (
          <WeeklyPlanner
            meals={meals}
            plan={plan}
            setPlan={setPlan}
            onLockIn={lockInWeek}
            onAddGrocery={addDaysToGroceryList}
            onRemoveGrocery={removeDaysFromGroceryList}
            weekStart={weekStart}
            weekOffset={weekOffset}
            onPrevWeek={() => setWeekOffset((o) => o - 1)}
            onNextWeek={() => setWeekOffset((o) => o + 1)}
            onJumpToday={() => setWeekOffset(0)}
            isMobile={isMobile}
            onCook={setCookMeal}
          />
        )}
        {view === "grocery" && <GroceryList items={groceryItems} checked={checked} setChecked={setChecked} onReset={() => setChecked({})} onClearList={clearGroceryList} extras={extras} onAddExtra={addExtra} onRemoveExtra={removeExtra} isMobile={isMobile} />}
        {view === "ai" && <AskAI meals={meals} plan={plan} onApplyPlan={applyAIPlan} onAddMealToBox={addAIMealToBox} onScannedMeal={handleScannedMeal} />}
      </main>

      {isMobile && (
        <nav className="no-print" style={{ position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", background: COLOR.rail, borderTop: `1px solid ${COLOR.divider}`, paddingBottom: "env(safe-area-inset-bottom, 0px)", zIndex: 50 }}>
          {NAV.map((n) => {
            const active = view === n.id; const Icon = n.icon;
            return (
              <button key={n.id} onClick={() => setView(n.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 4px", background: "transparent", border: "none", color: active ? COLOR.gold : "#C9BCA3" }}>
                <Icon size={20} />
                <span style={{ fontSize: 10, fontFamily: "'Inter', sans-serif", fontWeight: active ? 600 : 500 }}>{n.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {modalMeal && <MealModal meal={modalMeal} onSave={saveMeal} onClose={() => setModalMeal(null)} />}
      {cookMeal && <CookModeModal meal={cookMeal} onClose={() => setCookMeal(null)} />}

      {toast && <div className="fixed rounded-md shadow-lg text-sm" style={{ bottom: isMobile ? 84 : 24, right: isMobile ? 16 : 24, left: isMobile ? 16 : "auto", padding: "10px 16px", background: COLOR.ink, color: COLOR.paper, fontFamily: "'Inter', sans-serif" }}>{toast}</div>}
    </div>
  );
}
